import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { ArtifactRelationship } from '../../../types/artifactRelationship';
import { ArtifactStateTransitionType } from '../../../types/enums';
import './RadialView.css';

interface RadialViewProps {
  relationships: ArtifactRelationship[];
  onRelationshipSelect?: (relationshipId: string) => void;
}

export const RadialView: React.FC<RadialViewProps> = ({ relationships, onRelationshipSelect }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current || relationships.length === 0) return;

    // Clear previous visualization
    d3.select(svgRef.current).selectAll('*').remove();

    // Set up dimensions
    const width = svgRef.current.clientWidth;
    const height = svgRef.current.clientHeight;
    const radius = Math.min(width, height) / 2 - 100;

    // Create SVG group for centering
    const svg = d3.select(svgRef.current).attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${width / 2},${height / 2})`);

    // Create scales for positioning
    const angleScale = d3
      .scalePoint()
      .domain(relationships.map((_, i) => i.toString()))
      .range([0, 2 * Math.PI]);

    // Create color scale for relationship types
    const colorScale = d3
      .scaleOrdinal<ArtifactStateTransitionType, string>()
      .domain([
        ArtifactStateTransitionType.OPERATIONAL,
        ArtifactStateTransitionType.RESOURCE,
        ArtifactStateTransitionType.MATERIAL,
        ArtifactStateTransitionType.CREATION,
        ArtifactStateTransitionType.IMPROVEMENT,
      ])
      .range(['#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#9c755f']);

    // Create line generator for connections
    const lineGenerator = d3
      .lineRadial<{ angle: number; radius: number }>()
      .angle((d) => d.angle)
      .radius((d) => d.radius)
      .curve(d3.curveCardinalClosed);

    // Draw connections
    relationships.forEach((relationship, i) => {
      const angle = angleScale(i.toString()) || 0;

      // Draw connection line
      g.append('path')
        .datum([
          { angle: angle - 0.1, radius: 0 },
          { angle: angle + 0.1, radius: radius },
        ])
        .attr('fill', 'none')
        .attr('stroke', colorScale(relationship.relationship_type as ArtifactStateTransitionType))
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', relationship.direction === 'incoming' ? '5,5' : 'none')
        .attr('d', lineGenerator)
        .attr('class', 'relationship-line')
        .attr('data-relationship-id', relationship.id)
        .on('click', () => {
          if (onRelationshipSelect) {
            onRelationshipSelect(relationship.id);
          }
        });

      // Add relationship type label
      g.append('text')
        .attr('x', radius * 0.8 * Math.cos(angle))
        .attr('y', radius * 0.8 * Math.sin(angle))
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('class', 'relationship-label')
        .text(relationship.relationship_type.replace('_', ' '))
        .attr('fill', colorScale(relationship.relationship_type as ArtifactStateTransitionType));

      // Add artifact name
      const artifact =
        relationship.direction === 'incoming'
          ? relationship.source_artifact
          : relationship.target_artifact;

      if (artifact) {
        g.append('text')
          .attr('x', radius * 1.1 * Math.cos(angle))
          .attr('y', radius * 1.1 * Math.sin(angle))
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('class', 'artifact-name')
          .text(artifact.name);
      }
    });

    // Add central node
    g.append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', 20)
      .attr('fill', '#333')
      .attr('class', 'central-node');

    // Add zoom behavior
    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 5])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom as d3.ZoomBehavior<SVGSVGElement, unknown>);

    // Add tooltips
    g.selectAll('.relationship-line')
      .on('mouseover', function (event) {
        const relationship = relationships.find(
          (r) => r.id === (this as Element).getAttribute('data-relationship-id')
        );
        if (relationship) {
          d3.select(this).attr('stroke-width', 4);

          // Show tooltip
          const tooltip = d3
            .select('body')
            .append('div')
            .attr('class', 'tooltip')
            .style('position', 'absolute')
            .style('background-color', 'white')
            .style('padding', '5px')
            .style('border', '1px solid #ddd')
            .style('border-radius', '3px')
            .style('pointer-events', 'none')
            .style('opacity', 0);

          tooltip.transition().duration(200).style('opacity', 1);

          tooltip
            .html(
              `
            <strong>${relationship.relationship_type.replace('_', ' ')}</strong><br/>
            Direction: ${relationship.direction}<br/>
            ${relationship.source_artifact ? `From: ${relationship.source_artifact.name}<br/>` : ''}
            ${relationship.target_artifact ? `To: ${relationship.target_artifact.name}` : ''}
          `
            )
            .style('left', event.pageX + 10 + 'px')
            .style('top', event.pageY - 28 + 'px');
        }
      })
      .on('mouseout', function () {
        d3.select(this).attr('stroke-width', 2);

        d3.selectAll('.tooltip').remove();
      });
  }, [relationships, onRelationshipSelect]);

  return (
    <div className="radial-view">
      <svg ref={svgRef}></svg>
    </div>
  );
};
